// Client-side waste permission mirror (Module 05 §7).
//
// The server is the ONLY authorisation boundary: `waste.catalog.manage` and
// `waste.settings.manage` are enforced by the route guards, and the §5 row-57
// role narrowing (group_admin / facility_admin / planner may READ the settings)
// is re-checked in the service. This hook exists so S11 does not OVER-SHOW
// controls a role cannot use — a facility_admin opening the settings screen
// should see the values, not a form that 403s on save.
//
// It derives the caller's effective `waste.*` codes from their role codes
// (`GET /v1/auth/tenants` — the only caller-role signal the client has),
// mirroring `systemRoleGrants` in packages/contracts. Custom (non-system) roles
// resolve to nothing and fall back to the server gate: graceful degradation,
// never a false allow. `has()` is FAIL-CLOSED — false until the feed settles,
// false if it fails.

import {
  derivePermissions,
  hasLineage,
  roleSignalOf,
  useRoleCodesFeed,
  type AreaPermissions,
  type RoleInput,
  type RoleSignal,
} from "../../../../lib/role-codes";

/** The `waste.*` codes slices 05-A + 05-B register (spec 05 §7). The other
 * seven §7 codes are registered by the slices that enforce them (05-C/05-D),
 * so they are absent here rather than mirrored as grants the server would not
 * honour. */
export const WASTE_PERMISSIONS = [
  "waste.read",
  "waste.catalog.manage",
  "waste.settings.manage",
  "waste.event.record",
  "waste.event.reverse",
  "waste.disposition.set",
  "waste.cost.manual",
  "waste.remnant.manage",
] as const;

export type WastePermission = (typeof WASTE_PERMISSIONS)[number];

/**
 * Role code → `waste.*` codes: the waste slice of the contracts
 * `systemRoleGrants` table (spec 05 §7), transcribed cell by cell.
 *
 * The rows that are easy to get wrong:
 * - group_admin holds all three; NOBODY ELSE holds a write code. §7's "Edit
 *   categories/causes/classes/settings" row gives facility_admin "targets only
 *   (own F)", i.e. recovery targets, not the catalogs.
 * - facility_admin / planner / finance / qc / maintenance hold `waste.read`
 *   (§7's "View ledger & variance" row). qc's "no cost" and maintenance's
 *   "machine variance only" are serializer/service narrowings, not codes.
 * - sales, procurement, storekeeper and floor_worker hold NOTHING here. They
 *   still read the CATALOGS, because §5 rows 1 and 61 are open to any
 *   authenticated principal — that read needs no code and is not modelled here.
 */
export const WASTE_ROLE_GRANTS: Readonly<Record<string, readonly string[]>> = {
  group_admin: [...WASTE_PERMISSIONS],
  // 05-B: facility_admin has ✓F on every ledger/remnant action row (§7).
  facility_admin: [
    "waste.read",
    "waste.event.record",
    "waste.event.reverse",
    "waste.disposition.set",
    "waste.cost.manual",
    "waste.remnant.manage",
  ],
  // Planner's remnant cell is "reserve/release F" — one code, per-action
  // narrowing service-enforced (D-040 two-layer).
  planner: ["waste.read", "waste.remnant.manage"],
  // Sales: "reserve/release/reclass F"; register/scrap refused service-side.
  sales: ["waste.remnant.manage"],
  procurement: [],
  // Finance records MANUAL events (§7 "✓ F (manual)"), reverses, dispositions,
  // and is one of the three roles on "Manual unit cost".
  finance: [
    "waste.read",
    "waste.event.record",
    "waste.event.reverse",
    "waste.disposition.set",
    "waste.cost.manual",
  ],
  // qc records (✓F), dispositions (✓F), registers remnants (✓F); no cost —
  // money is serializer-redacted, never a second code.
  qc: ["waste.read", "waste.event.record", "waste.disposition.set", "waste.remnant.manage"],
  maintenance: ["waste.read"],
  // Storekeeper: record, disposition and the whole remnant row; NO waste.read
  // (the "View ledger & variance" cell is "—").
  storekeeper: ["waste.event.record", "waste.disposition.set", "waste.remnant.manage"],
  // Floor worker: the §4 S8 capture (✓D) and remnant register/update (✓D).
  floor_worker: ["waste.event.record", "waste.remnant.manage"],
};

/** The effective `waste.*` set for a role-code set (pure, unit-tested). */
export function deriveWastePermissions(roles: RoleInput): Set<string> {
  return derivePermissions(roles, WASTE_ROLE_GRANTS);
}

/**
 * Roles the §5 row-57 settings READ admits, mirrored so the screen can say
 * "you may look, not change" instead of rendering a form that will 403.
 * Narrower than `waste.read` on purpose — the service enforces exactly this.
 */
const SETTINGS_READ_ROLES = new Set(["group_admin", "facility_admin", "planner"]);

/**
 * The settings read's two layers, as the server applies them: the route's
 * `waste.read` and the service's lineage set (`waste-settings.service.ts`
 * `get`, D-224 ruling 2). A tenant's clone of planner reads the settings; a
 * clone without `waste.read` does not (D-586).
 */
export function canReadWasteSettings(roles: RoleInput, codes: ReadonlySet<string>): boolean {
  return codes.has("waste.read") && hasLineage(roles, SETTINGS_READ_ROLES);
}

export interface WastePermissions extends AreaPermissions, RoleSignal {
  readonly roleCodes: ReadonlySet<string>;
  /** True when the role feed itself failed — the screen says so and lets the
   * request go, rendering the server's own refusal rather than a blank page. */
  readonly isFeedError: boolean;
  /** May GET /v1/waste/settings (spec 05 §5 row 57). */
  readonly canReadSettings: boolean;
}

export function useWastePermissions(): WastePermissions {
  const feed = useRoleCodesFeed();
  const codes = deriveWastePermissions(feed);
  return {
    isResolved: feed.isResolved,
    has: (code: string) => codes.has(code),
    ...roleSignalOf(feed),
    isFeedError: feed.isFeedError,
    canReadSettings: canReadWasteSettings(feed, codes),
  };
}

// ---------------------------------------------------------------------------
// THE AREA'S ENTRY TABLE — one rule, read by the app nav AND the sub-nav.
// ---------------------------------------------------------------------------
//
// The waste area was UNREACHABLE for five of the roles that own it. The app nav
// gated its single link on `canReadSettings` (group_admin / facility_admin /
// planner) and pointed it at `/waste/settings` — the catalogue editor — while
// the sub-nav below gated four tabs on three DIFFERENT codes. So:
//
//   finance      holds `waste.read`, `waste.event.reverse`,
//                `waste.disposition.set`, `waste.cost.manual` — §4 S2 lists
//                finance FIRST, and settling waste is finance's job — and had
//                no link into the area at all.
//   qc           holds `waste.read`. No link.
//   storekeeper  holds `waste.remnant.manage`, and this area's own shell says
//                "the registry is their surface, not the ledger". No link — so
//                the remnant registry was reachable by no role it was built for.
//   sales        same. No link.
//   planner      had a link, pointing at the editor rather than at the ledger.
//
// Two gating rules for one area is the defect. `wasteAreaEntries` IS the rule:
// the sub-nav renders it in order, the app nav shows a link iff it is non-empty
// and points at its FIRST entry, and `/waste` redirects there. A role that can
// reach nothing gets no link rather than a link into a 403.
//
// ORDER IS THE LANDING ORDER, and it is deliberate: the ledger first (the
// screen that answers a question without an id in hand), then the scoreboard,
// then the registry, then the settings editor last — a catalogue editor is
// where you go on purpose, never where you arrive.

export interface WasteAreaEntry {
  /** The route path. */
  readonly to: string;
  /** The dictionary key for its label (`waste.nav.*`). */
  readonly labelKey: string;
}

/** The permission facts the entry table reads — the shape both callers hold. */
export interface WasteEntryPermissions {
  readonly has: (code: string) => boolean;
  readonly canReadSettings: boolean;
}

/** The waste screens this principal may open, in landing order (pure). */
export function wasteAreaEntries(perms: WasteEntryPermissions): WasteAreaEntry[] {
  const entries: WasteAreaEntry[] = [];
  if (perms.has("waste.read")) {
    entries.push({ to: "/waste/events", labelKey: "waste.nav.ledger" });
    entries.push({ to: "/waste/shifts", labelKey: "waste.nav.shifts" });
    // S13 rides `waste.read` exactly as S2/S4 do — §4 S13's audience IS the
    // "View ledger & variance" row, and the money on it is redacted by the same
    // serializer rather than gated by a second code (§7's field-level rule).
    entries.push({ to: "/waste/pareto", labelKey: "waste.nav.pareto" });
  }
  if (perms.has("waste.remnant.manage")) {
    entries.push({ to: "/waste/remnants", labelKey: "waste.nav.remnants" });
  }
  if (perms.canReadSettings) {
    entries.push({ to: "/waste/settings", labelKey: "waste.nav.settings" });
  }
  return entries;
}

/** Where `/waste` (and the app-nav link) lands this principal, or `undefined`
 * when the area holds nothing for them. */
export function wasteLandingPath(perms: WasteEntryPermissions): string | undefined {
  return wasteAreaEntries(perms)[0]?.to;
}

/**
 * Is this principal REFUSED a waste read — a stable fact, not a failure?
 *
 * True only once the role feed has settled and answered. While it is in flight,
 * or when it FAILED, the answer is "let the request go and render the server's
 * own refusal" — the fail-open direction, because the mirror is a display aid
 * and the server is the authorisation boundary (D-040's two layers). It is the
 * same predicate `settings.tsx` already applies to `canReadSettings`.
 *
 * Callers use it twice: to keep the query from firing at all (a read that will
 * be refused should not be issued), and to render `ForbiddenState` instead of
 * an error card whose Retry can never succeed.
 */
export function isWasteReadRefused(perms: WastePermissions, code: WastePermission): boolean {
  return perms.isResolved && !perms.isFeedError && !perms.has(code);
}
